import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, mergeMap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { WINDOW } from 'src/app/feature/injection-token/window';

import { ApiRequestType } from 'src/app/shared/enums/api.enums';
import { validArray, validString } from 'src/app/shared/utilities/types.utils';
import { IS_IN_IFRAME } from 'src/app/shared/utilities/window.utils';
import { apiResponse, postApiRequest } from '../api/api.actions';
import { responseRequestType } from '../api/api.utilities';
import { GetMerchantDetailsResponse, MerchantCharac } from '../api/responses/get-merchant-details';
import { setLanguage } from '../client-configuration/client-configuration.actions';
import { selectLanguage } from '../client-configuration/client-configuration.selectors';
import { getQueryParams } from '../initialize/initialize.module';
import { fetchLocaleFile } from '../locale/locale.actions';
import { fullRouteTo, routeTo } from '../routing/routing.actions';
import {
	setDefaultLanguage,
	updateMerchantDetailsAction,
	updateOrderMerchantDetailsAction,
} from './merchant-details.actions';
import { Merchant } from './merchant-details.model';
import { buildMerchantModel } from './merchant-details.utils';
import { selectMerchantDetails } from './merchant-details.selectors';

const { GET_APPLICATION_CONFIG } = ApiRequestType;

@Injectable()
export class MerchantDetailsEffects {
	/**
	 * The currently configured language.
	 */
	private _language: string;

	/**
	 * Listens for the request the merchant from backend.
	 */
	loadMerchantDetails$ = createEffect(() =>
		this._actions$.pipe(
			ofType(apiResponse),
			filter(responseRequestType(ApiRequestType.GET_MERCHANT_DETAILS)),
			withLatestFrom(this._store$.select(selectMerchantDetails)),
			mergeMap(([action, merchantDetials]) => {
				const actions = [];

				if (!action.isOk) {
					actions.push(fullRouteTo({ payload: ['invalid'] }));
				} else {
					if (merchantDetials) {
						const orderMerchant: Merchant = buildMerchantModel(action.response);
						// Update the merchant state with orderMerchant details
						actions.push(updateOrderMerchantDetailsAction({ merchant: orderMerchant }));
						return actions;
					}
					const merchant: Merchant = buildMerchantModel(action.response);
					const defaultLanguage = getDefaultLocale(action.response);
					this._store$.dispatch(
						setDefaultLanguage({
							language: defaultLanguage,
						})
					);

					// If we don't have a language in the URL path, get the default language and set it.
					if (!this._language) {
						const language = defaultLanguage;
						actions.push(setLanguage({ payload: language }));
						actions.push(fetchLocaleFile({ payload: language }));
						actions.push(postApiRequest({ requestType: GET_APPLICATION_CONFIG }));
						if (!IS_IN_IFRAME && getQueryParams(this._window.location.search).t) {
							actions.push(routeTo({ payload: ['select'] }));
						}
						// Otherwise fetch the application configs.
					} else {
						actions.push(postApiRequest({ requestType: GET_APPLICATION_CONFIG }));
					}

					// Update the merchant
					actions.push(updateMerchantDetailsAction({ merchant }));
				}

				return actions;
			})
		)
	);

	/**
	 * @param _window the _window
	 * @param _actions$ Our stream of actions.
	 * @param _store$ The _store$ itself.
	 */
	constructor(@Inject(WINDOW) private _window: Window, private _actions$: Actions, private _store$: Store<AppState>) {
		this._store$.select(selectLanguage).subscribe((lang) => {
			this._language = lang;
		});
	}
}

/**
 * Gets the default_language merchant charac from the GetMerchantDetails response.
 * @param response The GetMerchantDetails response
 * @returns string
 */
export function getDefaultLocale(response: GetMerchantDetailsResponse): string {
	const characs = response.CHARAC;
	if (validArray(characs)) {
		const charac = characs.find((current: MerchantCharac) => current.key === 'ap_default_language');
		if (charac && validString(charac.value)) {
			return charac.value;
		}
	}

	return 'en-US';
}
